/**
 * AxiomOnboardingModal.tsx
 *
 * Axiom Connect clinic enrollment — a 4-step wizard (Entity → Licensing →
 * Billing & ACH → Review) matching the Axiom Connect design. Fields per the
 * Axiom "Clinic Onboarding Field Spec"; the Licensing step verifies the clinic
 * NPI against the public NPPES registry (via our server-side proxy). On submit
 * the full payload is stored encrypted server-side (POST /integrations/{key}/onboarding).
 */
import { useEffect, useRef, useState, useId } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useAuthStore } from '@/store/authStore';
import {
  submitIntegrationOnboarding,
  lookupNpi,
  type NpiLookupResult,
} from '@/services/integrations.service';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

interface OnboardingForm {
  legalEntityName: string; dba: string; ein: string;
  street: string; suite: string; city: string; state: string; zip: string;
  clinicNpi: string;
  contactEmail: string; contactPhone: string;
  contactName: string; billingEmail: string; billingPhone: string;
  authorizedSigner: string; signerTitle: string;
  bankName: string; accountHolderName: string; accountType: string; routingNumber: string; accountNumber: string;
  achAuthorization: boolean; acceptTerms: boolean;
}
const EMPTY: OnboardingForm = {
  legalEntityName: '', dba: '', ein: '', street: '', suite: '', city: '', state: '', zip: '',
  clinicNpi: '', contactEmail: '', contactPhone: '',
  contactName: '', billingEmail: '', billingPhone: '', authorizedSigner: '', signerTitle: '',
  bankName: '', accountHolderName: '', accountType: 'checking', routingNumber: '', accountNumber: '',
  achAuthorization: false, acceptTerms: false,
};

type NpiStatus = 'idle' | 'checking' | 'verified' | 'inactive' | 'notfound';

const STEPS = ['Entity', 'Licensing', 'Billing & ACH', 'Review'];

export function Field({ label, required, value, onChange, placeholder, type = 'text', inputMode, grow = true, hint }: {
  label: string; required?: boolean; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; inputMode?: 'numeric' | 'text' | 'email' | 'tel'; grow?: boolean;
  /** Info-icon tooltip shown next to the label. */
  hint?: string;
}) {
  // The label sits beside the input rather than wrapping it, so without an
  // explicit association a screen reader announces the field unnamed.
  const id = useId();
  return (
    <div className="axc-field" style={grow ? undefined : { flex: '0 0 auto' }}>
      <label className="axc-label" htmlFor={id}>
        {label}{required && <span className="axc-req">*</span>}
        {hint && (
          <span className="axc-hint" data-tip={hint} tabIndex={0} aria-label={hint}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden><circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.8"/><path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><circle cx="12" cy="7.6" r="1.2" fill="currentColor"/></svg>
          </span>
        )}
      </label>
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type} inputMode={inputMode} />
    </div>
  );
}

interface AxiomOnboardingModalProps {
  integrationKey: string;
  vendorName: string;
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

export function AxiomOnboardingModal({ integrationKey, vendorName, open, onClose, onCompleted }: AxiomOnboardingModalProps) {
  // Locked page (plan 2026-10-10-001 U7: R8, R18): the page offers no Join,
  // and the wizard itself renders nothing, so no Locked path can enroll.
  const locked = useAdminPageLevel() === 'locked';
  const accountId = useAuthStore((s) => s.accountId);
  const [step, setStep] = useState(1);
  const [f, setF] = useState<OnboardingForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Set when the local enrollment saved but the Axiom /clinics sync didn't go
  // through — surfaced as a soft note on the success screen (the first order retries).
  const [enrollWarning, setEnrollWarning] = useState<string | null>(null);

  const [npiStatus, setNpiStatus] = useState<NpiStatus>('idle');
  const [npiResult, setNpiResult] = useState<NpiLookupResult | null>(null);
  const [npiOverride, setNpiOverride] = useState(false);
  const npiSeq = useRef(0);

  const set = (k: keyof OnboardingForm, v: string | boolean) => { setF((p) => ({ ...p, [k]: v })); setError(null); };

  // Reset when reopened.
  useEffect(() => {
    if (!open) {
      setStep(1); setF(EMPTY); setError(null); setDone(false); setEnrollWarning(null);
      setNpiStatus('idle'); setNpiResult(null); setNpiOverride(false);
    }
  }, [open]);

  // Verify the NPI (debounced) whenever it becomes 10 digits.
  useEffect(() => {
    const digits = f.clinicNpi.replace(/[^0-9]/g, '');
    setNpiOverride(false);
    if (digits.length !== 10) { setNpiStatus('idle'); setNpiResult(null); return; }
    const seq = ++npiSeq.current;
    setNpiStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await lookupNpi(digits);
        if (seq !== npiSeq.current) return;
        setNpiResult(r);
        setNpiStatus(!r.found ? 'notfound' : r.active ? 'verified' : 'inactive');
      } catch {
        if (seq !== npiSeq.current) return;
        setNpiStatus('notfound'); setNpiResult(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [f.clinicNpi]);

  const submit = useMutation({
    mutationFn: () => submitIntegrationOnboarding(integrationKey, f as unknown as Record<string, unknown>),
    onSuccess: (res) => {
      // axiomEnrolled === false means the attestation didn't reach Axiom; the
      // local enrollment still saved and the first order retries the sync.
      if (res.axiomEnrolled === false) {
        setEnrollWarning(res.axiomError || "Saved — but syncing with Axiom didn't complete. We'll retry automatically on your first order.");
      }
      setDone(true);
    },
    onError: (e) => setError(getErrorMessage(e, 'Could not submit enrollment. Please try again.')),
  });

  if (!open || locked) return null;

  function validateStep(s: number): string | null {
    if (s === 1) {
      if (!f.legalEntityName.trim()) return 'Legal entity name is required.';
      if (!f.ein.trim()) return 'EIN is required.';
      if (!f.street.trim() || !f.city.trim() || !f.state || !f.zip.trim()) return 'A complete business address is required.';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.contactEmail)) return 'Enter a valid contact email.';
      if (!f.contactPhone.trim()) return 'Contact phone is required.';
    }
    if (s === 2) {
      if (f.clinicNpi.replace(/[^0-9]/g, '').length !== 10) return 'Enter a valid 10-digit NPI.';
      if (npiStatus === 'checking') return 'Verifying NPI…';
      if (npiStatus !== 'verified' && !npiOverride) return 'This NPI could not be verified. Review it, or continue anyway.';
    }
    if (s === 3) {
      for (const [k, lbl] of [['contactName','Contact name'],['billingEmail','Billing email'],['billingPhone','Billing phone'],['authorizedSigner','Authorized signer'],['signerTitle','Signer title'],['accountHolderName',"Bank Account Holder's Name"],['bankName','Bank name'],['routingNumber','Routing number'],['accountNumber','Account number']] as const) {
        if (!String(f[k]).trim()) return `${lbl} is required.`;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.billingEmail)) return 'Enter a valid billing email.';
      if (!f.achAuthorization) return 'You must authorize ACH debits.';
    }
    if (s === 4 && !f.acceptTerms) return 'You must accept the enrollment terms.';
    return null;
  }

  function next() {
    const msg = validateStep(step);
    if (msg) { setError(msg); return; }
    setError(null);
    setStep((s) => Math.min(4, s + 1));
  }
  function back() { setError(null); setStep((s) => Math.max(1, s - 1)); }
  function handleSubmit() {
    const msg = validateStep(4);
    if (msg) { setError(msg); return; }
    setError(null);
    submit.mutate();
  }

  const npiOverrideMode = step === 2 && npiResult != null && (npiStatus === 'notfound' || npiStatus === 'inactive') && !npiOverride;

  return (
    <div className="axc-root">
      <style>{AXC_CSS}</style>
      <div className="axc-overlay" onClick={done ? undefined : onClose}>
        <div className="axc-modal" role="dialog" aria-modal="true" aria-label="Join Axiom Connect" onClick={(e) => e.stopPropagation()}>
          <div className="axc-modal-head">
            <div className="axc-brand">
              <div className="axc-mark" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M8 13l2.5 2.5L16 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div>
                <div className="axc-brand-name">{vendorName} Connect</div>
                <div className="axc-brand-sub">Clinic enrollment via Prospera</div>
              </div>
            </div>
            <button className="axc-close" aria-label="Close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </button>
          </div>

          {!done && (
            <div className="axc-stepper">
              {STEPS.map((label, i) => {
                const n = i + 1;
                const cls = n === step ? 'is-active' : n < step ? 'is-done' : '';
                return (
                  <div key={label} className={`axc-step ${cls}`}>
                    <div className="axc-step-dot">{n < step ? '✓' : n}</div>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          )}

          {done ? (
            <div className="axc-success">
              <div className="axc-success-mark">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <h2>You're enrolled</h2>
              <p>Your clinic is enrolled in {vendorName} Connect. You can now set your prices and start ordering compounded cartridges with automatic ACH billing.</p>
              <div className="axc-success-meta">
                <div><span>Clinic</span><strong>{f.legalEntityName || '—'}</strong></div>
                <div><span>Prospera account</span><strong>#{accountId ?? '—'}</strong></div>
                <div><span>Status</span><strong className="axc-live">Active</strong></div>
              </div>
              {enrollWarning && (
                <p className="axc-enroll-warn" role="status">{enrollWarning}</p>
              )}
              <button className="axc-btn-primary axc-success-btn" onClick={onCompleted}>Set prices</button>
            </div>
          ) : (
            <>
              <div className="axc-modal-body">
                <div className="axc-attr">
                  <span className="axc-attr-dot" />
                  <span>Enrolling under <strong>{f.legalEntityName || 'your clinic'}</strong> · Prospera account <code>#{accountId ?? '—'}</code></span>
                </div>

                {error && <div className="axc-err-line" style={{ marginBottom: 12 }}>{error}</div>}

                {step === 1 && (
                  <div className="axc-section">
                    <h4>Business entity</h4>
                    <p className="axc-section-desc">Legal information for your clinic, used for contracting and billing.</p>
                    <div className="axc-fields">
                      <Field label="Legal entity name" required value={f.legalEntityName} onChange={(v) => set('legalEntityName', v)} placeholder="Lakeshore Wellness LLC" />
                      <div className="axc-row">
                        <Field label="DBA (if different)" value={f.dba} onChange={(v) => set('dba', v)} placeholder="Lakeshore Longevity" />
                        <Field label="EIN" required value={f.ein} onChange={(v) => set('ein', v)} placeholder="12-3456789" inputMode="numeric" />
                      </div>
                      <Field label="Street address" required value={f.street} onChange={(v) => set('street', v)} placeholder="120 Lakeshore Dr" />
                      <Field label="Suite / unit (optional)" value={f.suite} onChange={(v) => set('suite', v)} />
                      <div className="axc-row">
                        <Field label="City" required value={f.city} onChange={(v) => set('city', v)} placeholder="Birmingham" />
                        <div className="axc-field" style={{ flex: '0 0 auto' }}>
                          <label className="axc-label">State<span className="axc-req">*</span></label>
                          <select value={f.state} onChange={(e) => set('state', e.target.value)}>
                            <option value="">—</option>
                            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <Field label="ZIP" required value={f.zip} onChange={(v) => set('zip', v)} placeholder="48009" inputMode="numeric" grow={false} />
                      </div>
                      <div className="axc-row">
                        <Field label="Contact email" required value={f.contactEmail} onChange={(v) => set('contactEmail', v)} placeholder="office@lakeshorewellness.com" type="email" inputMode="email" hint="The clinic's main contact email — used for enrollment and order communication." />
                        <Field label="Contact phone" required value={f.contactPhone} onChange={(v) => set('contactPhone', v)} placeholder="(248) 555-0100" inputMode="tel" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="axc-section">
                    <h4>Licensing</h4>
                    <p className="axc-section-desc">We verify your clinic's NPI against the national registry (NPPES).</p>
                    <div className="axc-fields">
                      <div className="axc-field axc-npi-field">
                        <label className="axc-label">
                          <span>Clinic NPI<span className="axc-req">*</span></span>
                          {npiStatus === 'checking' && <span className="axc-npi-pill axc-npi-checking"><span className="axc-spin" /> Checking…</span>}
                          {npiStatus === 'verified' && <span className="axc-npi-pill axc-npi-verified"><span className="axc-pill-dot" /> Verified</span>}
                          {npiStatus === 'inactive' && <span className="axc-npi-pill axc-npi-inactive"><span className="axc-pill-dot" /> Inactive</span>}
                          {npiStatus === 'notfound' && <span className="axc-npi-pill axc-npi-notfound"><span className="axc-pill-dot" /> Not found</span>}
                        </label>
                        <input
                          value={f.clinicNpi}
                          onChange={(e) => set('clinicNpi', e.target.value)}
                          placeholder="10-digit NPI"
                          inputMode="numeric"
                          maxLength={10}
                          className={npiStatus === 'verified' ? 'axc-input-ok' : (npiStatus === 'inactive' || npiStatus === 'notfound') ? 'axc-input-bad' : undefined}
                        />
                      </div>
                      {npiStatus === 'verified' && npiResult && (
                        <div className="axc-npi-card axc-npi-card-ok">
                          <span className="axc-npi-icon">✓</span>
                          <div><strong>{npiResult.name || 'Registered provider'}</strong><span>{[npiResult.city, npiResult.state].filter(Boolean).join(', ')} · NPI active in NPPES</span></div>
                        </div>
                      )}
                      {npiStatus === 'inactive' && (
                        <div className="axc-npi-card axc-npi-card-warn">
                          <span className="axc-npi-icon">!</span>
                          <div><strong>{npiResult?.name || 'NPI is deactivated'}</strong><span>This NPI is marked inactive in NPPES. You can continue, but Axiom may follow up.</span></div>
                        </div>
                      )}
                      {npiStatus === 'notfound' && (
                        <div className="axc-npi-card axc-npi-card-bad">
                          <span className="axc-npi-icon">✕</span>
                          <div><strong>No match in NPPES</strong><span>Double-check the number. You can continue anyway and Axiom will verify manually.</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="axc-section">
                    <h4>Billing &amp; ACH</h4>
                    <p className="axc-section-desc">Billing contact and the bank account Axiom will debit for orders.</p>
                    <div className="axc-fields">
                      <div className="axc-row">
                        <Field label="Contact name" required value={f.contactName} onChange={(v) => set('contactName', v)} />
                        <Field label="Billing email" required value={f.billingEmail} onChange={(v) => set('billingEmail', v)} type="email" inputMode="email" />
                      </div>
                      <div className="axc-row">
                        <Field label="Billing phone" required value={f.billingPhone} onChange={(v) => set('billingPhone', v)} inputMode="tel" />
                        <Field label="Authorized signer" required value={f.authorizedSigner} onChange={(v) => set('authorizedSigner', v)} />
                        <Field label="Signer title" required value={f.signerTitle} onChange={(v) => set('signerTitle', v)} />
                      </div>
                      <div className="axc-subhead axc-subhead-spacer">Bank account (ACH) <span className="axc-mt-badge">Encrypted</span></div>
                      <Field
                        label="Bank Account Holder's Name"
                        required
                        value={f.accountHolderName}
                        onChange={(v) => set('accountHolderName', v)}
                        placeholder="Exactly as it appears on your bank statement"
                        hint="This may be a person or your legal entity name — enter it exactly as it appears on your bank statement."
                      />
                      <div className="axc-row">
                        <Field label="Bank name" required value={f.bankName} onChange={(v) => set('bankName', v)} />
                        <div className="axc-field" style={{ flex: '0 0 auto' }}>
                          <label className="axc-label">Account type<span className="axc-req">*</span></label>
                          <select value={f.accountType} onChange={(e) => set('accountType', e.target.value)}>
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                          </select>
                        </div>
                      </div>
                      <div className="axc-row">
                        <Field label="Routing number" required value={f.routingNumber} onChange={(v) => set('routingNumber', v)} inputMode="numeric" />
                        <Field label="Account number" required value={f.accountNumber} onChange={(v) => set('accountNumber', v)} inputMode="numeric" />
                      </div>
                      <label className={`axc-check axc-auth ${error && !f.achAuthorization ? 'has-error' : ''}`}>
                        <input type="checkbox" checked={f.achAuthorization} onChange={(e) => set('achAuthorization', e.target.checked)} />
                        <span>I authorize {vendorName} to initiate ACH debits from the account above for orders placed by this clinic.</span>
                      </label>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="axc-section">
                    <h4>Review &amp; submit</h4>
                    <p className="axc-section-desc">Confirm your details, then accept the enrollment terms.</p>
                    <div className="axc-review">
                      <ReviewBlock title="Entity" onEdit={() => setStep(1)} rows={[
                        ['Legal entity', f.legalEntityName], ['DBA', f.dba || '—'], ['EIN', f.ein],
                        ['Address', [f.street, f.suite, `${f.city}, ${f.state} ${f.zip}`].filter(Boolean).join(', ')],
                        ['Contact', [f.contactEmail, f.contactPhone].filter(Boolean).join(' · ')],
                      ]} />
                      <ReviewBlock title="Licensing" onEdit={() => setStep(2)} rows={[
                        ['Clinic NPI', f.clinicNpi], ['Verification', npiStatus === 'verified' ? 'Verified' : npiOverride ? 'Manual (override)' : '—'],
                      ]} />
                      <ReviewBlock title="Billing & ACH" onEdit={() => setStep(3)} rows={[
                        ['Contact', f.contactName], ['Billing email', f.billingEmail], ['Authorized signer', `${f.authorizedSigner}${f.signerTitle ? `, ${f.signerTitle}` : ''}`],
                        ['Account holder', f.accountHolderName],
                        ['Bank', f.bankName], ['Account', `${f.accountType} ••••${f.accountNumber.slice(-4)}`],
                      ]} />
                    </div>
                    <div className="axc-terms">
                      <div className="axc-terms-scroll">
                        <strong>{vendorName} Connect Enrollment Terms</strong>
                        <p>By enrolling, your clinic authorizes {vendorName} to fulfill compounded cartridge orders placed through Prospera and to debit the bank account on file via ACH for amounts due.</p>
                        <p>You confirm the information provided is accurate and that you are an authorized representative of the clinic with authority to bind it to these terms.</p>
                        <p>ACH authorization remains in effect until revoked in writing. Bank details are stored encrypted.</p>
                      </div>
                    </div>
                    <label className="axc-check">
                      <input type="checkbox" checked={f.acceptTerms} onChange={(e) => set('acceptTerms', e.target.checked)} />
                      <span>I have read and accept the {vendorName} Connect enrollment terms, and I am an authorized representative of this clinic.</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="axc-modal-foot">
                <div className="axc-foot-left">
                  {step > 1 && <button className="axc-btn-ghost" onClick={back} disabled={submit.isPending}>Back</button>}
                </div>
                <div className="axc-foot-right">
                  <span className="axc-foot-step">Step {step} of 4</span>
                  {step < 4 ? (
                    npiOverrideMode ? (
                      <button className="axc-btn-primary axc-btn-override" onClick={() => { setNpiOverride(true); setError(null); setStep(3); }}>Continue anyway</button>
                    ) : (
                      <button className="axc-btn-primary" onClick={next} disabled={step === 2 && npiStatus === 'checking'}>Continue</button>
                    )
                  ) : (
                    <button className="axc-btn-primary" onClick={handleSubmit} disabled={submit.isPending}>
                      {submit.isPending ? 'Submitting…' : 'Submit enrollment'}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, onEdit, rows }: { title: string; onEdit: () => void; rows: Array<[string, string]> }) {
  return (
    <div className="axc-review-block">
      <div className="axc-review-head">{title}<button className="axc-edit" onClick={onEdit}>Edit</button></div>
      <dl>{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v || '—'}</dd></div>)}</dl>
    </div>
  );
}

/** Shared Axiom Connect theme — also used by AxiomBankingModal so the two
 *  clinic-facing enrollment surfaces stay visually identical. */
export const AXC_CSS = `
.axc-root{--ink:#0F1E2E;--ink-2:#3A4A5A;--muted:#6B7C8C;--line:#E2E8EE;--paper:#FFFFFF;--canvas:#F5F8FA;--field:#FBFCFD;--accent:#0B6E6E;--accent-deep:#085757;--accent-wash:#E7F2F2;--danger:#B23A48;--danger-wash:#FBEEF0;--ok:#0B6E6E;--r:10px;--r-sm:7px;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:var(--ink);-webkit-font-smoothing:antialiased;}
.axc-root *{box-sizing:border-box;}
.axc-overlay{position:fixed;inset:0;background:rgba(15,30,46,.5);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:20px;z-index:1000;animation:axc-fade .18s ease;}
@keyframes axc-fade{from{opacity:0;}to{opacity:1;}}
.axc-modal{width:100%;max-width:640px;max-height:92vh;background:var(--paper);border-radius:16px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 70px -20px rgba(15,30,46,.45);animation:axc-rise .22s cubic-bezier(.2,.7,.3,1);}
@keyframes axc-rise{from{opacity:0;transform:translateY(12px) scale(.99);}to{opacity:1;transform:none;}}
.axc-modal-head{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid var(--line);flex:0 0 auto;}
.axc-brand{display:flex;align-items:center;gap:12px;}
.axc-mark{width:38px;height:38px;border-radius:9px;background:var(--accent-wash);color:var(--accent);display:flex;align-items:center;justify-content:center;}
.axc-brand-name{font-size:15px;font-weight:680;letter-spacing:-.01em;}
.axc-brand-sub{font-size:12px;color:var(--muted);margin-top:1px;}
.axc-close{width:32px;height:32px;border-radius:8px;border:1px solid var(--line);background:var(--paper);color:var(--ink-2);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:.15s;}
.axc-close:hover{background:var(--canvas);color:var(--ink);}
.axc-stepper{display:flex;align-items:center;gap:6px;padding:14px 22px;background:var(--canvas);border-bottom:1px solid var(--line);flex:0 0 auto;overflow-x:auto;}
.axc-step{display:flex;align-items:center;gap:8px;flex:0 0 auto;padding-right:6px;}
.axc-step:not(:last-child)::after{content:"";width:18px;height:1.5px;background:var(--line);margin-left:2px;}
.axc-step-dot{width:24px;height:24px;border-radius:50%;border:1.5px solid var(--line);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:var(--muted);background:var(--paper);flex:0 0 auto;transition:.2s;}
.axc-step span{font-size:12.5px;font-weight:550;color:var(--muted);white-space:nowrap;transition:.2s;}
.axc-step.is-active .axc-step-dot{border-color:var(--accent);color:var(--accent);background:var(--accent-wash);}
.axc-step.is-active span{color:var(--ink);font-weight:620;}
.axc-step.is-done .axc-step-dot{background:var(--accent);border-color:var(--accent);color:#fff;}
.axc-step.is-done span{color:var(--ink-2);}
.axc-modal-body{padding:20px 22px;overflow-y:auto;flex:1 1 auto;}
.axc-attr{display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--ink-2);background:var(--accent-wash);border:1px solid #CDE6E6;border-radius:var(--r-sm);padding:9px 12px;margin-bottom:18px;}
.axc-attr code{background:rgba(11,110,110,.12);padding:1px 6px;border-radius:4px;font-size:11.5px;color:var(--accent-deep);}
.axc-attr-dot{width:7px;height:7px;border-radius:50%;background:var(--accent);flex:0 0 auto;box-shadow:0 0 0 3px rgba(11,110,110,.16);}
.axc-section h4{margin:0 0 4px;font-size:16px;font-weight:650;letter-spacing:-.01em;}
.axc-section-desc{margin:0 0 16px;font-size:13px;color:var(--muted);line-height:1.5;}
.axc-fields{display:flex;flex-direction:column;gap:14px;}
.axc-row{display:flex;gap:12px;}
.axc-row .axc-field{flex:1;}
.axc-field{display:flex;flex-direction:column;gap:6px;min-width:0;}
.axc-label{font-size:12.5px;font-weight:560;color:var(--ink-2);}
.axc-req{color:var(--accent);margin-left:2px;}
.axc-hint{position:relative;display:inline-flex;align-items:center;margin-left:5px;color:var(--muted);cursor:help;vertical-align:-1px;}
.axc-hint:hover,.axc-hint:focus-visible{color:var(--accent);}
.axc-hint::after{content:attr(data-tip);position:absolute;bottom:calc(100% + 7px);left:50%;transform:translateX(-50%);width:230px;background:var(--ink);color:#fff;font-size:11.5px;font-weight:450;line-height:1.45;padding:8px 10px;border-radius:7px;box-shadow:0 6px 20px rgba(2,6,23,.25);opacity:0;pointer-events:none;transition:opacity .15s;z-index:30;white-space:normal;}
.axc-hint:hover::after,.axc-hint:focus-visible::after{opacity:1;}
.axc-field input,.axc-field select{width:100%;border:1px solid var(--line);background:var(--field);border-radius:var(--r-sm);padding:10px 12px;font-size:14px;color:var(--ink);font-family:inherit;transition:.15s;}
.axc-field input::placeholder{color:#A9B7C4;}
.axc-field input:focus,.axc-field select:focus{outline:none;border-color:var(--accent);background:var(--paper);box-shadow:0 0 0 3px rgba(11,110,110,.13);}
.axc-subhead{font-size:12px;font-weight:660;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);display:flex;align-items:center;gap:10px;}
.axc-subhead-spacer{margin-top:8px;padding-top:16px;border-top:1px dashed var(--line);}
.axc-mt-badge{font-size:10.5px;font-weight:600;letter-spacing:.02em;text-transform:none;background:var(--accent-wash);color:var(--accent-deep);padding:3px 9px;border-radius:20px;border:1px solid #CDE6E6;}
.axc-check{display:flex;align-items:flex-start;gap:10px;cursor:pointer;font-size:13px;color:var(--ink-2);line-height:1.5;}
.axc-check input{margin-top:1px;width:16px;height:16px;accent-color:var(--accent);flex:0 0 auto;cursor:pointer;}
.axc-auth{background:var(--canvas);border:1px solid var(--line);border-radius:var(--r-sm);padding:12px 14px;}
.axc-auth.has-error{border-color:var(--danger);background:var(--danger-wash);}
.axc-err-line{font-size:12px;color:var(--danger);font-weight:500;}
.axc-npi-field .axc-label{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.axc-npi-pill{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:3px 9px;border-radius:20px;letter-spacing:.01em;transition:.2s;}
.axc-pill-dot{width:6px;height:6px;border-radius:50%;background:currentColor;flex:0 0 auto;}
.axc-npi-checking{background:#EEF3F7;color:#5A6B7B;}
.axc-npi-verified{background:var(--accent-wash);color:var(--accent-deep);}
.axc-npi-inactive,.axc-npi-notfound{background:var(--danger-wash);color:var(--danger);}
.axc-spin{width:11px;height:11px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;display:inline-block;animation:axc-spin .6s linear infinite;opacity:.7;}
@keyframes axc-spin{to{transform:rotate(360deg);}}
.axc-input-ok{border-color:var(--accent)!important;background:var(--paper)!important;}
.axc-input-bad{border-color:var(--danger)!important;background:var(--danger-wash)!important;}
.axc-npi-card{display:flex;align-items:flex-start;gap:9px;border-radius:var(--r-sm);padding:10px 12px;margin-top:2px;animation:axc-npi-in .22s ease;}
@keyframes axc-npi-in{from{opacity:0;transform:translateY(-3px);}to{opacity:1;transform:none;}}
.axc-npi-card strong{display:block;font-size:12.5px;font-weight:620;margin-bottom:2px;}
.axc-npi-card span{font-size:11.5px;line-height:1.45;}
.axc-npi-icon{flex:0 0 auto;margin-top:1px;font-weight:700;}
.axc-npi-card-ok{background:var(--accent-wash);border:1px solid #CDE6E6;}
.axc-npi-card-ok strong{color:var(--accent-deep);}.axc-npi-card-ok span{color:var(--ink-2);}.axc-npi-card-ok .axc-npi-icon{color:var(--accent);}
.axc-npi-card-warn{background:#FBF3E4;border:1px solid #EAD9B0;}
.axc-npi-card-warn strong{color:#8A5E10;}.axc-npi-card-warn span{color:#6E5827;}.axc-npi-card-warn .axc-npi-icon{color:#C08A1E;}
.axc-npi-card-bad{background:var(--danger-wash);border:1px solid #E8C9CE;}
.axc-npi-card-bad strong{color:var(--danger);}.axc-npi-card-bad span{color:#8A4048;}.axc-npi-card-bad .axc-npi-icon{color:var(--danger);}
.axc-review{display:flex;flex-direction:column;gap:12px;margin-bottom:18px;}
.axc-review-block{border:1px solid var(--line);border-radius:var(--r-sm);overflow:hidden;}
.axc-review-head{display:flex;align-items:center;justify-content:space-between;background:var(--canvas);padding:9px 14px;font-size:12.5px;font-weight:640;color:var(--ink);border-bottom:1px solid var(--line);}
.axc-edit{background:none;border:none;color:var(--accent);font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;padding:2px 4px;}
.axc-edit:hover{text-decoration:underline;}
.axc-review-block dl{margin:0;padding:6px 14px 10px;}
.axc-review-block dl>div{display:flex;gap:14px;padding:5px 0;font-size:13px;align-items:baseline;}
.axc-review-block dt{flex:0 0 130px;color:var(--muted);font-size:12px;}
.axc-review-block dd{margin:0;color:var(--ink);font-weight:500;word-break:break-word;}
.axc-terms{margin-bottom:16px;}
.axc-terms-scroll{max-height:148px;overflow-y:auto;border:1px solid var(--line);border-radius:var(--r-sm);padding:14px 16px;background:var(--field);font-size:12.5px;line-height:1.6;color:var(--ink-2);}
.axc-terms-scroll strong{display:block;color:var(--ink);margin-bottom:8px;font-size:13px;}
.axc-terms-scroll p{margin:0 0 8px;}
.axc-modal-foot{display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-top:1px solid var(--line);background:var(--paper);flex:0 0 auto;gap:12px;}
.axc-foot-right{display:flex;align-items:center;gap:14px;}
.axc-foot-step{font-size:12px;color:var(--muted);}
.axc-btn-primary{background:var(--accent);color:#fff;border:none;border-radius:var(--r-sm);padding:11px 20px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;box-shadow:0 1px 2px rgba(11,110,110,.2),0 6px 14px -8px rgba(11,110,110,.5);transition:.15s;}
.axc-btn-primary:hover{background:var(--accent-deep);}
.axc-btn-primary:disabled{opacity:.55;cursor:not-allowed;}
.axc-btn-override{background:#C0851E;box-shadow:0 1px 2px rgba(192,133,30,.2),0 6px 14px -8px rgba(192,133,30,.55);}
.axc-btn-override:hover{background:#A06E14;}
.axc-btn-ghost{background:none;border:1px solid var(--line);color:var(--ink-2);border-radius:var(--r-sm);padding:11px 18px;font-size:14px;font-weight:550;cursor:pointer;font-family:inherit;transition:.15s;}
.axc-btn-ghost:hover{background:var(--canvas);color:var(--ink);}
.axc-success{padding:44px 32px 30px;text-align:center;display:flex;flex-direction:column;align-items:center;}
.axc-success-mark{width:64px;height:64px;border-radius:50%;background:var(--accent-wash);color:var(--accent);display:flex;align-items:center;justify-content:center;margin-bottom:20px;animation:axc-pop .4s cubic-bezier(.2,.8,.3,1.2);}
@keyframes axc-pop{0%{transform:scale(.6);opacity:0;}60%{transform:scale(1.08);}100%{transform:scale(1);opacity:1;}}
.axc-success h2{margin:0 0 8px;font-size:21px;font-weight:680;letter-spacing:-.02em;}
.axc-success>p{margin:0 0 22px;font-size:14px;line-height:1.55;color:var(--muted);max-width:400px;}
.axc-success-meta{width:100%;max-width:380px;border:1px solid var(--line);border-radius:var(--r);overflow:hidden;margin-bottom:22px;}
.axc-success-meta>div{display:flex;justify-content:space-between;align-items:center;padding:11px 16px;font-size:13px;}
.axc-success-meta>div:not(:last-child){border-bottom:1px solid var(--line);}
.axc-enroll-warn{width:100%;max-width:380px;margin:-6px 0 18px;padding:10px 14px;border:1px solid #f5d7a8;background:#fdf6ea;border-radius:var(--r);color:#8a5a12;font-size:12.5px;line-height:1.5;text-align:left;}
.axc-success-meta span{color:var(--muted);}
.axc-success-meta strong{color:var(--ink);font-weight:600;}
.axc-live{color:var(--ok)!important;display:inline-flex;align-items:center;gap:6px;}
.axc-live::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--ok);box-shadow:0 0 0 3px rgba(11,110,110,.18);}
.axc-success-btn{min-width:140px;}
@media(max-width:560px){.axc-row{flex-direction:column;gap:14px;}.axc-step span{display:none;}.axc-step:not(:last-child)::after{width:12px;}.axc-modal-foot{flex-direction:column-reverse;align-items:stretch;}.axc-foot-right{justify-content:space-between;}.axc-btn-primary{flex:1;}}
@media(prefers-reduced-motion:reduce){.axc-overlay,.axc-modal,.axc-success-mark{animation:none;}}
`;
